/**
 * THE ONE FILE A SECOND APP REPLACES. Everything else in this folder is copied unchanged.
 *
 * ## Why TypeScript and not JSON
 *
 * `satisfies WhatsNewEntry[]` means a misspelled `kind`, a missing `title` or a stray field
 * breaks the build instead of landing silently in the panel in front of the pilot. What
 * `satisfies` cannot see — a duplicate `id`, a malformed `date`, an `image` that no endpoint
 * serves, an `anchor` nobody mounts — `scripts/scan-whats-new-contract.mjs` checks on every run
 * of `npm run scan`. Same posture as the other eight guards under `scripts/`: what can be
 * checked is checked, not remembered.
 *
 * ## What earns an entry
 *
 * **Only a change a user can notice.** Not every phase, not every deploy. A refactor, a
 * migration, a new guard, a corrected grant: no entry. A column that appeared, a word that
 * changed on a status, a button that moved: an entry. A dot that lights up for things nobody can
 * see is wallpaper within three months, and then it fails to light up for the one thing that
 * mattered.
 *
 * The decision is made out loud at every phase close — `.claude/skills/close-phase` step 5b asks
 * "can a user notice the difference?" and either drafts an entry or says there is none, and
 * **only Roman confirms the wording** before it commits. Several sessions run in this tree at
 * once and an entry is an app-wide message nobody can take back.
 *
 * ## How to write one
 *
 * English (house rule), one to three sentences, what the reader GETS — not what was built. No
 * phase numbers, no file names, no endpoints, no table names. If a sentence stops making sense
 * once those are removed, it was about the work and not about the change. No apology and no
 * roadmap: an entry describes what is live today.
 *
 * `id` is stable, dated and never reused: it is the key of a row in `[Dra].[WhatsNewSeen]`, so
 * changing one re-shows the entry to everybody who already saw it, and reusing one hides a new
 * entry from everybody who saw the old one. `date` is the day it reached the users, not the day
 * it was written.
 *
 * `major: true` shows a blocking dialog once, in front of whatever somebody sat down to do. The
 * bar is "they would be worse off not knowing before they start", and it is met a couple of times
 * a year. **Both entries below are `false`**, and that is a decision rather than caution: neither
 * changes what anybody must DO, and the first thing this module ever did should not be to block
 * the four people in the pilot.
 */

import type { WhatsNewEntry } from './types';

export const entries = [
    {
        id: '2026-09-30-boards-and-screens-redrawn',
        date: '2026-09-30',
        kind: 'improved',
        major: false,
        title: 'The lists fit your screen',
        body:
            'The ticket list and the review queue now show every column at once, in rows of one '
            + 'height, with the header staying put while you scroll. You can open a ticket with '
            + 'the keyboard, and the Columns button lets you choose which ones you want to see.',
        anchor: 'control:columns',
    },
    {
        id: '2026-09-30-approved-but-not-sent-says-so',
        date: '2026-09-30',
        kind: 'fixed',
        major: false,
        title: 'A reply that was approved but not sent',
        body:
            'When a reply you approved could not go out, the ticket now says "Approved, not '
            + 'sent" and shows who approved it and when — instead of still reading as though it '
            + 'were waiting for you. Those tickets are in the review queue and in the counter '
            + 'above it, so none of them sits unnoticed.',
    },
    {
        id: '2026-10-01-whats-new',
        date: '2026-10-01',
        kind: 'new',
        major: false,
        title: "What's New",
        body:
            "The DRA now keeps a short list of what has changed, behind the What's New button in "
            + 'the header. A dot appears when there is something you have not read yet, and it '
            + 'follows you between your laptop and your phone.',
        // No `image`, deliberately. The origin's own entry for this module carries
        // `image: 'placeholder'`, which is a grey box — it exists so the endpoint and its
        // catalogue are real before any screenshot has been taken, not so a reader looks at it.
        // The capture script is not ported (it knows the origin app's tab keys), so attaching it
        // here would put a meaningless picture in front of the pilot. The endpoint is proven by
        // `__tests__/images.test.ts`, which holds the catalogue against the bytes on disk.
    },
] satisfies WhatsNewEntry[];
